import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useCompany } from '../hooks/useCompany'
import { MetricCard } from '../components/MetricCard'
import { ClockInOutCard } from '../components/ClockInOutCard'
import { WeekChart } from '../components/WeekChart'
import { CheckCircle, X } from 'lucide-react'

interface DashboardData {
  team: {
    id: string
    name: string
    tier: number
  }
  metrics: {
    memberCount: number
    activeMembers: number
    weekHours: number
    isClockedIn: boolean
    lastClockTime: string | null
  }
  weekData: Array<{ day: string; hours: number }>
}

export default function Dashboard() {
  const { } = useAuth()
  const { currentCompany } = useCompany()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showWelcome, setShowWelcome] = useState(false)
  const [showInviteError, setShowInviteError] = useState(false)
  const [welcomeClosing, setWelcomeClosing] = useState(false)
  const [inviteErrorClosing, setInviteErrorClosing] = useState(false)
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null)

  // Check for invitation status from URL params
  useEffect(() => {
    const invitationAccepted = searchParams.get('invitation_accepted')
    const inviteError = searchParams.get('invite_error')
    
    if (invitationAccepted === 'true') {
      setShowWelcome(true)
      // Trigger confetti animation
      triggerConfetti()
      
      // Clean up URL
      window.history.replaceState({}, '', '/dashboard')
    }
    
    if (inviteError === 'true') {
      setShowInviteError(true)
      // Auto-hide after 8 seconds
      setTimeout(() => setShowInviteError(false), 8000)
      
      // Clean up URL
      window.history.replaceState({}, '', '/dashboard')
    }
  }, [searchParams])

  // Smooth close functions
  const handleWelcomeClose = () => {
    setWelcomeClosing(true)
    setTimeout(() => {
      setShowWelcome(false)
      setWelcomeClosing(false)
    }, 300) // Match the CSS transition duration
  }

  const handleInviteErrorClose = () => {
    setInviteErrorClosing(true)
    setTimeout(() => {
      setShowInviteError(false)
      setInviteErrorClosing(false)
    }, 300) // Match the CSS transition duration
  }

  // Enhanced confetti animation function with popper-like explosion
  const triggerConfetti = () => {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9']
    const shapes = ['square', 'circle', 'triangle', 'rectangle']
    const confettiCount = 80
    
    // Create multiple explosion points for more dramatic effect
    const explosionPoints = [
      { x: '25%', y: '60%' },
      { x: '50%', y: '50%' },
      { x: '75%', y: '60%' }
    ]
    
    explosionPoints.forEach((point, pointIndex) => {
      setTimeout(() => {
        for (let i = 0; i < confettiCount / explosionPoints.length; i++) {
          const confetti = document.createElement('div')
          const shape = shapes[Math.floor(Math.random() * shapes.length)]
          const color = colors[Math.floor(Math.random() * colors.length)]
          const size = 8 + Math.random() * 6 // 8-14px
          
          // Base styles
          confetti.style.position = 'fixed'
          confetti.style.width = size + 'px'
          confetti.style.height = size + 'px'
          confetti.style.background = color
          confetti.style.left = point.x
          confetti.style.top = point.y
          confetti.style.zIndex = '9999'
          confetti.style.pointerEvents = 'none'
          
          // Shape-specific styling
          switch (shape) {
            case 'circle':
              confetti.style.borderRadius = '50%'
              break
            case 'triangle':
              confetti.style.width = '0'
              confetti.style.height = '0'
              confetti.style.background = 'transparent'
              confetti.style.borderLeft = `${size/2}px solid transparent`
              confetti.style.borderRight = `${size/2}px solid transparent`
              confetti.style.borderBottom = `${size}px solid ${color}`
              break
            case 'rectangle':
              confetti.style.width = size * 1.5 + 'px'
              confetti.style.height = size * 0.6 + 'px'
              confetti.style.borderRadius = '2px'
              break
            case 'square':
            default:
              confetti.style.borderRadius = '2px'
              break
          }
          
          document.body.appendChild(confetti)
          
          // Calculate explosion trajectory (outward from center with some randomness)
          const angle = (Math.random() * 2 * Math.PI) // Random angle in radians
          const velocity = 150 + Math.random() * 200 // Random velocity
          const gravity = 800 // Pixels per second squared
          const duration = 2500 + Math.random() * 1500 // 2.5-4 seconds
          
          // Calculate final position based on physics
          const finalX = Math.cos(angle) * velocity * (duration / 1000)
          const finalY = Math.sin(angle) * velocity * (duration / 1000) + 0.5 * gravity * Math.pow(duration / 1000, 2)
          
          // Animate with realistic physics
          const animation = confetti.animate([
            { 
              transform: 'translate(0, 0) rotate(0deg) scale(1)', 
              opacity: 1 
            },
            { 
              transform: `translate(${finalX}px, ${finalY}px) rotate(${Math.random() * 720 - 360}deg) scale(0.8)`, 
              opacity: 0.3,
              offset: 0.8
            },
            { 
              transform: `translate(${finalX}px, ${finalY + 100}px) rotate(${Math.random() * 1080 - 540}deg) scale(0.5)`, 
              opacity: 0 
            }
          ], {
            duration: duration,
            easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' // Smooth deceleration
          })
          
          animation.onfinish = () => confetti.remove()
        }
      }, pointIndex * 150) // Stagger explosions by 150ms
    })
  }

  // Fetch teams for current company
  useEffect(() => {
    const fetchTeamsAndSetCurrent = async () => {
      if (!currentCompany?.id) {
        setCurrentTeamId(null)
        return
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/v1/teams?company_id=${currentCompany.id}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch teams')
        }

        const data = await response.json()
        const teams = data.teams || []
        
        // Find a team where the user is a member (user_role is not null)
        const userTeam = teams.find((team: any) => team.user_role !== null)
        
        if (userTeam) {
          setCurrentTeamId(userTeam.id)
        } else if (teams.length > 0) {
          // Fallback to first team if no membership found
          setCurrentTeamId(teams[0].id)
        } else {
          setCurrentTeamId(null)
          setError('No teams found in this company')
        }
      } catch (err) {
        console.error('Failed to fetch teams:', err)
        setCurrentTeamId(null)
      }
    }

    fetchTeamsAndSetCurrent()
  }, [currentCompany?.id])

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!currentTeamId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/v1/dashboard/metrics?team_id=${currentTeamId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch dashboard data')
        }

        const data = await response.json()
        setDashboardData(data)
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load dashboard')
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [currentTeamId])

  const handleClockIn = async () => {
    // Navigate to the clock page instead of clocking in directly
    navigate('/time/clock')
  }

  const handleClockOut = async () => {
    if (!currentTeamId) return

    try {
      setError(null)

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/timesheets/clock-out`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            method: 'FREE'
          })
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to clock out')
      }

      // Refresh dashboard data after clocking out
      window.location.reload() // Quick refresh to update all data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clock out')
    } finally {
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-24 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load dashboard</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    )
  }

  // No company selected state
  if (!currentCompany) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m6 0v-5a2 2 0 00-2-2H7a2 2 0 00-2 2v5m6-5V9a1 1 0 00-1-1H4a1 1 0 00-1 1v2a1 1 0 001 1h3m0-6h2a1 1 0 011 1v4a1 1 0 01-1 1H6a1 1 0 01-1-1V7a1 1 0 011-1z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Company Selected</h3>
        <p className="text-gray-600 mb-4">Please select a company from the sidebar to view your dashboard.</p>
        <button 
          onClick={() => window.location.href = '/onboarding'}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Create Company
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Welcome Message for Invitation Acceptance */}
      {showWelcome && (
        <div 
          className={`bg-green-50 border border-green-200 rounded-lg p-4 relative overflow-hidden transition-all duration-300 ease-in-out ${
            welcomeClosing 
              ? 'max-h-0 opacity-0 py-0 my-0 border-transparent' 
              : 'max-h-96 opacity-100'
          }`}
        >
          <div className="flex items-center">
            <CheckCircle className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-green-900">
                Welcome to {currentCompany?.name}! 🎉
              </h3>
              <p className="text-green-700 mt-1">
                Your invitation has been accepted successfully. You're now part of the team!
              </p>
            </div>
            <button
              onClick={handleWelcomeClose}
              className="text-green-400 hover:text-green-600 ml-4 flex-shrink-0 transition-colors duration-200"
              aria-label="Close welcome message"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Invitation Error Message */}
      {showInviteError && (
        <div 
          className={`bg-yellow-50 border border-yellow-200 rounded-lg p-4 relative overflow-hidden transition-all duration-300 ease-in-out ${
            inviteErrorClosing 
              ? 'max-h-0 opacity-0 py-0 my-0 border-transparent' 
              : 'max-h-96 opacity-100'
          }`}
        >
          <div className="flex items-center">
            <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
              <span className="text-white text-sm font-bold">!</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-yellow-900">
                Invitation Issue
              </h3>
              <p className="text-yellow-700 mt-1">
                There was an issue accepting your invitation, but you've been signed in successfully. Please contact your team administrator if you need access to team features.
              </p>
            </div>
            <button
              onClick={handleInviteErrorClose}
              className="text-yellow-400 hover:text-yellow-600 ml-4 flex-shrink-0 transition-colors duration-200"
              aria-label="Close invitation error message"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Team Summary Section */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Team Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <MetricCard
            title="Team Members"
            value={dashboardData?.metrics.memberCount.toString() || '0'}
            className="w-full"
          />
          <MetricCard
            title="Plan Tier"
            value={`Tier ${dashboardData?.team.tier || 1}`}
            className="w-full"
          />
        </div>
      </div>

      {/* Clock In/Out Section */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Clock In/Out</h2>
        <ClockInOutCard
          isClocked={dashboardData?.metrics.isClockedIn || false}
          onClockIn={handleClockIn}
          onClockOut={handleClockOut}
          lastClockTime={dashboardData?.metrics.lastClockTime || undefined}
        />
      </div>

      {/* Timesheet Overview */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Timesheet Overview</h2>
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Hours Worked This Week</h3>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {dashboardData?.metrics.weekHours || 0}
              </p>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                This Week
              </div>
            </div>
          </div>
          {dashboardData?.weekData && dashboardData.weekData.length > 0 ? (
            <WeekChart weekData={dashboardData.weekData} />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-sm">No time entries this week</p>
            </div>
          )}
        </div>
      </div>

      {/* Team Activity */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Team Activity</h2>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h4 className="text-sm font-medium text-gray-900">Active Team Members</h4>
              <p className="text-sm text-gray-600 mt-1">
                {dashboardData?.metrics.activeMembers || 0} of {dashboardData?.metrics.memberCount || 0} members are currently clocked in.
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}